import {
  buildSchedulerBootstrapScopeKey,
  isSchedulerBootstrapPayload,
  isSchedulerSingleWeekScope,
  normalizeSchedulerBootstrapUnitIds,
  type SchedulerBootstrapPayload,
  type SchedulerBootstrapScope,
} from '../scheduler/bootstrap.ts';

const DB_NAME = 'mintleaf-scheduler';
const DB_VERSION = 2;
const STORE_NAME = 'bootstrap-snapshots';
const USER_ID_INDEX = 'by-user-id';
const SNAPSHOT_HARD_TTL_MS = 5 * 60 * 1000;
const SNAPSHOT_RETENTION_TTL_MS = 7 * 24 * 60 * 60 * 1000;
const HOT_SNAPSHOT_LIMIT = 12;
const SESSION_WARM_SNAPSHOT_LIMIT = 6;
const SESSION_WARM_AUTHORITY_LEASE_MS = 60 * 1000;

export type SchedulerSnapshotRecord = {
  scopeKey: string;
  userId: string;
  accessProfileVersion: number;
  permissionVersion: string;
  scheduleRevision: string;
  etag: string;
  savedAt: number;
  payload: SchedulerBootstrapPayload;
};

const memoryFallback = new Map<string, SchedulerSnapshotRecord>();
const hotSnapshots = new Map<string, SchedulerSnapshotRecord>();
const sessionWarmSnapshots = new Map<string, SchedulerSnapshotRecord>();
let hotSnapshotSessionUserId: string | null = null;
let hotSnapshotSessionAccessProfileVersion: number | null = null;
let sessionAuthorizationLease: {
  userId: string;
  accessProfileVersion: number;
  permissionVersion: string;
  validatedAt: number;
} | null = null;
let databasePromise: Promise<IDBDatabase | null> | null = null;

const cloneRecord = (record: SchedulerSnapshotRecord): SchedulerSnapshotRecord =>
  typeof structuredClone === 'function'
    ? structuredClone(record)
    : JSON.parse(JSON.stringify(record)) as SchedulerSnapshotRecord;

const rememberHotSnapshot = (record: SchedulerSnapshotRecord): void => {
  hotSnapshots.delete(record.scopeKey);
  hotSnapshots.set(record.scopeKey, record);
  while (hotSnapshots.size > HOT_SNAPSHOT_LIMIT) {
    const oldestScopeKey = hotSnapshots.keys().next().value as string | undefined;
    if (!oldestScopeKey) break;
    hotSnapshots.delete(oldestScopeKey);
  }
};

const recordIsSingleWeek = (record: SchedulerSnapshotRecord): boolean =>
  isSchedulerSingleWeekScope({
    fromDateKey: record.payload.fromDateKey,
    toDateKey: record.payload.toDateKey,
    weekStartDateKey: record.payload.weekStartDateKey,
  });

const rememberSessionWarmSnapshot = (record: SchedulerSnapshotRecord): void => {
  if (
    !recordIsSingleWeek(record) ||
    !hotSnapshotSessionUserId ||
    hotSnapshotSessionUserId !== record.userId ||
    hotSnapshotSessionAccessProfileVersion !== record.accessProfileVersion
  ) {
    return;
  }
  sessionWarmSnapshots.delete(record.scopeKey);
  sessionWarmSnapshots.set(record.scopeKey, record);
  while (sessionWarmSnapshots.size > SESSION_WARM_SNAPSHOT_LIMIT) {
    const oldestScopeKey = sessionWarmSnapshots.keys().next().value as string | undefined;
    if (!oldestScopeKey) break;
    sessionWarmSnapshots.delete(oldestScopeKey);
  }
};

export const clearSchedulerSessionWarmSnapshots = (): void => {
  sessionWarmSnapshots.clear();
  sessionAuthorizationLease = null;
};

export const setSchedulerHotSnapshotSessionIdentity = (
  userId: string | null | undefined,
  accessProfileVersion?: number | null
): void => {
  const normalizedUserId = typeof userId === 'string' ? userId.trim() : '';
  const nextUserId = normalizedUserId || null;
  const nextAccessProfileVersion =
    typeof accessProfileVersion === 'number' && Number.isFinite(accessProfileVersion)
      ? accessProfileVersion
      : null;
  if (
    hotSnapshotSessionUserId === nextUserId &&
    hotSnapshotSessionAccessProfileVersion === nextAccessProfileVersion
  ) {
    return;
  }
  hotSnapshots.clear();
  clearSchedulerSessionWarmSnapshots();
  hotSnapshotSessionUserId = nextUserId;
  hotSnapshotSessionAccessProfileVersion = nextAccessProfileVersion;
};

export const setSchedulerSessionAuthorizationLease = ({
  userId,
  accessProfileVersion,
  permissionVersion,
  validatedAt = Date.now(),
}: {
  userId: string;
  accessProfileVersion: number;
  permissionVersion: string;
  validatedAt?: number;
}): void => {
  const normalizedUserId = userId.trim();
  const normalizedPermissionVersion = permissionVersion.trim();
  if (
    !normalizedUserId ||
    !Number.isFinite(accessProfileVersion) ||
    !normalizedPermissionVersion ||
    !Number.isFinite(validatedAt)
  ) {
    clearSchedulerSessionWarmSnapshots();
    return;
  }
  if (
    hotSnapshotSessionUserId !== normalizedUserId ||
    hotSnapshotSessionAccessProfileVersion !== accessProfileVersion
  ) {
    setSchedulerHotSnapshotSessionIdentity(normalizedUserId, accessProfileVersion);
  }
  if (
    sessionAuthorizationLease &&
    sessionAuthorizationLease.permissionVersion !== normalizedPermissionVersion
  ) {
    sessionWarmSnapshots.clear();
  }
  sessionAuthorizationLease = {
    userId: normalizedUserId,
    accessProfileVersion,
    permissionVersion: normalizedPermissionVersion,
    validatedAt,
  };
};

const openDatabase = (): Promise<IDBDatabase | null> => {
  if (typeof indexedDB === 'undefined') return Promise.resolve(null);
  if (databasePromise) return databasePromise;

  databasePromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const database = request.result;
      const store = database.objectStoreNames.contains(STORE_NAME)
        ? request.transaction!.objectStore(STORE_NAME)
        : database.createObjectStore(STORE_NAME, { keyPath: 'scopeKey' });
      if (!store.indexNames.contains(USER_ID_INDEX)) {
        store.createIndex(USER_ID_INDEX, 'userId', { unique: false });
      }
    };
    request.onsuccess = () => {
      const database = request.result;
      database.onversionchange = () => {
        database.close();
        databasePromise = null;
      };
      resolve(database);
    };
    request.onerror = () => {
      databasePromise = null;
      reject(request.error || new Error('Failed to open scheduler snapshot database.'));
    };
    request.onblocked = () => {
      databasePromise = null;
      resolve(null);
    };
  });

  return databasePromise;
};

const readIndexedDbRecord = async (scopeKey: string): Promise<SchedulerSnapshotRecord | null> => {
  const database = await openDatabase().catch(() => null);
  if (!database) return memoryFallback.get(scopeKey) ? cloneRecord(memoryFallback.get(scopeKey)!) : null;

  return new Promise<SchedulerSnapshotRecord | null>((resolve, reject) => {
    const transaction = database.transaction(STORE_NAME, 'readonly');
    const request = transaction.objectStore(STORE_NAME).get(scopeKey);
    request.onsuccess = () => resolve((request.result as SchedulerSnapshotRecord | undefined) ?? null);
    request.onerror = () => reject(request.error || new Error('Failed to read scheduler snapshot.'));
  });
};

const writeIndexedDbRecord = async (record: SchedulerSnapshotRecord): Promise<void> => {
  const database = await openDatabase().catch(() => null);
  if (!database) {
    memoryFallback.set(record.scopeKey, cloneRecord(record));
    return;
  }

  await new Promise<void>((resolve, reject) => {
    const transaction = database.transaction(STORE_NAME, 'readwrite');
    transaction.objectStore(STORE_NAME).put(record);
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error || new Error('Failed to write scheduler snapshot.'));
    transaction.onabort = () => reject(transaction.error || new Error('Scheduler snapshot write was aborted.'));
  });
};

const deleteIndexedDbRecord = async (scopeKey: string): Promise<void> => {
  memoryFallback.delete(scopeKey);
  const database = await openDatabase().catch(() => null);
  if (!database) return;

  await new Promise<void>((resolve, reject) => {
    const transaction = database.transaction(STORE_NAME, 'readwrite');
    transaction.objectStore(STORE_NAME).delete(scopeKey);
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error || new Error('Failed to delete scheduler snapshot.'));
  });
};

const normalizeOptionalScopePart = (value: string | null | undefined): string | null => {
  const normalized = typeof value === 'string' ? value.trim() : '';
  return normalized || null;
};

const isSchedulerSnapshotScopeSafe = ({
  record,
  scope,
  expectedAccessProfileVersion,
}: {
  record: SchedulerSnapshotRecord;
  scope: SchedulerBootstrapScope;
  expectedAccessProfileVersion: number | null | undefined;
}): boolean => {
  if (
    typeof expectedAccessProfileVersion !== 'number' ||
    !Number.isFinite(expectedAccessProfileVersion)
  ) {
    return false;
  }

  const scopeKey = buildSchedulerBootstrapScopeKey(scope);
  const payload = record.payload;
  if (!isSchedulerBootstrapPayload(payload)) return false;
  const expectedUnitIds = normalizeSchedulerBootstrapUnitIds(scope.unitIds);
  if (expectedUnitIds.length === 0) return false;
  const payloadUnitIds = normalizeSchedulerBootstrapUnitIds(payload.unitIds || []);
  const hasExactUnits =
    expectedUnitIds.length === payloadUnitIds.length &&
    expectedUnitIds.every((unitId, index) => unitId === payloadUnitIds[index]);
  const everyUnitRemainsReadable = expectedUnitIds.every(
    unitId =>
      payload.permissions.byUnitId?.[unitId]?.canRead === true &&
      payload.accessModeByUnitId?.[unitId] !== 'denied'
  );

  return (
    record.scopeKey === scopeKey &&
    record.userId === scope.userId &&
    record.accessProfileVersion === expectedAccessProfileVersion &&
    typeof record.etag === 'string' &&
    record.etag.length > 0 &&
    payload.scopeKey === scopeKey &&
    payload.userId === scope.userId &&
    hasExactUnits &&
    normalizeOptionalScopePart(payload.divisionId) ===
      normalizeOptionalScopePart(scope.divisionId) &&
    payload.fromDateKey === scope.fromDateKey &&
    payload.toDateKey === scope.toDateKey &&
    payload.weekStartDateKey === scope.weekStartDateKey &&
    payload.noteMode === scope.noteMode &&
    payload.accessProfileVersion === expectedAccessProfileVersion &&
    payload.permissionVersion === record.permissionVersion &&
    payload.scheduleRevision === record.scheduleRevision &&
    payload.permissions.canRead === true &&
    everyUnitRemainsReadable
  );
};

export const isSchedulerSnapshotTrustedForScope = ({
  record,
  scope,
  expectedAccessProfileVersion,
  now = Date.now(),
}: {
  record: SchedulerSnapshotRecord;
  scope: SchedulerBootstrapScope;
  expectedAccessProfileVersion: number | null | undefined;
  now?: number;
}): boolean =>
  isSchedulerSnapshotScopeSafe({ record, scope, expectedAccessProfileVersion }) &&
  typeof record.savedAt === 'number' &&
  now >= record.savedAt &&
  now - record.savedAt <= SNAPSHOT_HARD_TTL_MS;

export const isSchedulerSnapshotRetainedForScope = ({
  record,
  scope,
  expectedAccessProfileVersion,
  now = Date.now(),
}: {
  record: SchedulerSnapshotRecord;
  scope: SchedulerBootstrapScope;
  expectedAccessProfileVersion: number | null | undefined;
  now?: number;
}): boolean => {
  if (
    !isSchedulerSingleWeekScope(scope) ||
    !isSchedulerSnapshotScopeSafe({ record, scope, expectedAccessProfileVersion }) ||
    typeof record.savedAt !== 'number' ||
    now < record.savedAt ||
    now - record.savedAt > SNAPSHOT_RETENTION_TTL_MS
  ) {
    return false;
  }

  const expectedUnitIds = normalizeSchedulerBootstrapUnitIds(scope.unitIds);
  return (
    record.payload.delegatedPublishedOnlyUnitIds.length === 0 &&
    expectedUnitIds.every(
      unitId => record.payload.accessModeByUnitId?.[unitId] === 'native'
    )
  );
};

export const isSchedulerSessionWarmSnapshotSafeForScope = ({
  record,
  scope,
  expectedAccessProfileVersion,
  now = Date.now(),
}: {
  record: SchedulerSnapshotRecord;
  scope: SchedulerBootstrapScope;
  expectedAccessProfileVersion: number | null | undefined;
  now?: number;
}): boolean => {
  if (
    !isSchedulerSingleWeekScope(scope) ||
    !isSchedulerSnapshotScopeSafe({ record, scope, expectedAccessProfileVersion })
  ) {
    return false;
  }
  if (
    !sessionAuthorizationLease ||
    sessionAuthorizationLease.userId !== record.userId ||
    sessionAuthorizationLease.accessProfileVersion !== record.accessProfileVersion ||
    sessionAuthorizationLease.permissionVersion !== record.permissionVersion ||
    now < sessionAuthorizationLease.validatedAt ||
    now - sessionAuthorizationLease.validatedAt > SESSION_WARM_AUTHORITY_LEASE_MS
  ) {
    return false;
  }

  const expectedUnitIds = normalizeSchedulerBootstrapUnitIds(scope.unitIds);
  return (
    record.payload.delegatedPublishedOnlyUnitIds.length === 0 &&
    expectedUnitIds.every(
      unitId => record.payload.accessModeByUnitId?.[unitId] === 'native'
    )
  );
};

export const getSchedulerSessionWarmSnapshot = ({
  scope,
  expectedAccessProfileVersion,
  now = Date.now(),
}: {
  scope: SchedulerBootstrapScope;
  expectedAccessProfileVersion: number | null | undefined;
  now?: number;
}): SchedulerSnapshotRecord | null => {
  const scopeKey = buildSchedulerBootstrapScopeKey(scope);
  const record = sessionWarmSnapshots.get(scopeKey);
  if (!record) return null;
  if (!isSchedulerSessionWarmSnapshotSafeForScope({
    record,
    scope,
    expectedAccessProfileVersion,
    now,
  })) {
    return null;
  }
  rememberSessionWarmSnapshot(record);
  return record;
};

export const getSchedulerHotSnapshot = ({
  scope,
  expectedAccessProfileVersion,
  now = Date.now(),
}: {
  scope: SchedulerBootstrapScope;
  expectedAccessProfileVersion: number | null | undefined;
  now?: number;
}): SchedulerSnapshotRecord | null => {
  const scopeKey = buildSchedulerBootstrapScopeKey(scope);
  const record = hotSnapshots.get(scopeKey);
  if (!record) return null;
  if (!isSchedulerSnapshotTrustedForScope({
    record,
    scope,
    expectedAccessProfileVersion,
    now,
  })) {
    hotSnapshots.delete(scopeKey);
    return null;
  }
  // Map insertion order is the deterministic LRU order. Keep the original
  // payload reference so an ETag 304 does not churn the mounted scheduler.
  rememberHotSnapshot(record);
  rememberSessionWarmSnapshot(record);
  return record;
};

export const getSchedulerSnapshot = async ({
  scope,
  expectedAccessProfileVersion,
  now = Date.now(),
}: {
  scope: SchedulerBootstrapScope;
  expectedAccessProfileVersion: number | null | undefined;
  now?: number;
}): Promise<SchedulerSnapshotRecord | null> => {
  if (typeof expectedAccessProfileVersion !== 'number' || !Number.isFinite(expectedAccessProfileVersion)) {
    return null;
  }

  // Only same-page, server-established memory state is renderable here. Disk
  // age/APV alone cannot authorize a new page session; retained reads below
  // return candidates that require fresh server validation.
  const record = getSchedulerHotSnapshot({ scope, expectedAccessProfileVersion, now }) ||
    getSchedulerSessionWarmSnapshot({ scope, expectedAccessProfileVersion, now });
  return record ? cloneRecord(record) : null;
};

export const getSchedulerRetainedSnapshot = async ({
  scope,
  expectedAccessProfileVersion,
  now = Date.now(),
}: {
  scope: SchedulerBootstrapScope;
  expectedAccessProfileVersion: number | null | undefined;
  now?: number;
}): Promise<SchedulerSnapshotRecord | null> => {
  if (typeof expectedAccessProfileVersion !== 'number' || !Number.isFinite(expectedAccessProfileVersion)) {
    return null;
  }

  const scopeKey = buildSchedulerBootstrapScopeKey(scope);
  const record = await readIndexedDbRecord(scopeKey).catch(() => null);
  if (!record) return null;

  if (!isSchedulerSnapshotRetainedForScope({
    record,
    scope,
    expectedAccessProfileVersion,
    now,
  })) {
    await deleteIndexedDbRecord(scopeKey).catch(() => undefined);
    return null;
  }

  // This is only a retained candidate. Callers must perform fresh server-side
  // authority/revision validation before rendering it.
  return cloneRecord(record);
};

export const setSchedulerSnapshot = async (
  payload: SchedulerBootstrapPayload,
  etag: string,
  now = Date.now()
): Promise<void> => {
  if (!payload.permissions.canRead || !etag) return;

  const record: SchedulerSnapshotRecord = {
    scopeKey: payload.scopeKey,
    userId: payload.userId,
    accessProfileVersion: payload.accessProfileVersion,
    permissionVersion: payload.permissionVersion,
    scheduleRevision: payload.scheduleRevision,
    etag,
    savedAt: now,
    payload,
  };
  const payloadScope: SchedulerBootstrapScope = {
    userId: payload.userId,
    unitIds: payload.unitIds,
    divisionId: payload.divisionId,
    fromDateKey: payload.fromDateKey,
    toDateKey: payload.toDateKey,
    weekStartDateKey: payload.weekStartDateKey,
    noteMode: payload.noteMode,
  };
  if (!isSchedulerSnapshotTrustedForScope({
    record,
    scope: payloadScope,
    expectedAccessProfileVersion: payload.accessProfileVersion,
    now,
  })) return;
  rememberHotSnapshot(record);
  rememberSessionWarmSnapshot(record);
  await writeIndexedDbRecord(record);
};

export const deleteSchedulerSnapshot = async (scope: SchedulerBootstrapScope): Promise<void> => {
  const scopeKey = buildSchedulerBootstrapScopeKey(scope);
  hotSnapshots.delete(scopeKey);
  sessionWarmSnapshots.delete(scopeKey);
  await deleteIndexedDbRecord(scopeKey);
};


export const deleteSchedulerSnapshotsForUser = async (userId: string): Promise<void> => {
  const normalizedUserId = userId.trim();
  if (!normalizedUserId) return;

  for (const [scopeKey, record] of memoryFallback.entries()) {
    if (record.userId === normalizedUserId) memoryFallback.delete(scopeKey);
  }
  for (const [scopeKey, record] of hotSnapshots.entries()) {
    if (record.userId === normalizedUserId) hotSnapshots.delete(scopeKey);
  }
  for (const [scopeKey, record] of sessionWarmSnapshots.entries()) {
    if (record.userId === normalizedUserId) sessionWarmSnapshots.delete(scopeKey);
  }
  if (sessionAuthorizationLease?.userId === normalizedUserId) {
    sessionAuthorizationLease = null;
  }

  const database = await openDatabase().catch(() => null);
  if (!database || typeof IDBKeyRange === 'undefined') return;

  await new Promise<void>((resolve, reject) => {
    const transaction = database.transaction(STORE_NAME, 'readwrite');
    const cursorRequest = transaction
      .objectStore(STORE_NAME)
      .index(USER_ID_INDEX)
      .openKeyCursor(IDBKeyRange.only(normalizedUserId));
    cursorRequest.onsuccess = () => {
      const cursor = cursorRequest.result;
      if (!cursor) return;
      transaction.objectStore(STORE_NAME).delete(cursor.primaryKey);
      cursor.continue();
    };
    cursorRequest.onerror = () => reject(cursorRequest.error || new Error('Failed to enumerate scheduler snapshots.'));
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error || new Error('Failed to clear scheduler snapshots.'));
    transaction.onabort = () => reject(transaction.error || new Error('Scheduler snapshot clear was aborted.'));
  });
};

export const __schedulerHotSnapshotLimit = HOT_SNAPSHOT_LIMIT;
export const __schedulerSessionWarmSnapshotLimit = SESSION_WARM_SNAPSHOT_LIMIT;
export const __schedulerSessionWarmAuthorityLeaseMs = SESSION_WARM_AUTHORITY_LEASE_MS;
export const __getSchedulerHotSnapshotScopeKeysForTests = (): string[] =>
  Array.from(hotSnapshots.keys());
export const __getSchedulerSessionWarmScopeKeysForTests = (): string[] =>
  Array.from(sessionWarmSnapshots.keys());
export const __resetSchedulerHotSnapshotsForTests = (): void => {
  hotSnapshots.clear();
  sessionWarmSnapshots.clear();
  hotSnapshotSessionUserId = null;
  hotSnapshotSessionAccessProfileVersion = null;
  sessionAuthorizationLease = null;
};

export const __schedulerSnapshotHardTtlMs = SNAPSHOT_HARD_TTL_MS;
export const __schedulerSnapshotRetentionTtlMs = SNAPSHOT_RETENTION_TTL_MS;
export const __resetSchedulerSnapshotMemoryForTests = (): void => {
  memoryFallback.clear();
  __resetSchedulerHotSnapshotsForTests();
};
